import React, { useState, useEffect } from 'react';
import { Phone, Bell, BellOff, Save, X, TestTube } from 'lucide-react';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { useAuth } from '../../contexts/AuthContext';

interface AdminSettingsData {
  sms_phone_number: string;
  notify_on_trial: boolean;
  notify_on_payment: boolean;
  notify_on_cancellation: boolean;
}

export const AdminSettings: React.FC = () => {
  const { session } = useAuth();
  const [settings, setSettings] = useState<AdminSettingsData>({
    sms_phone_number: '',
    notify_on_trial: true,
    notify_on_payment: true,
    notify_on_cancellation: true,
  });

  const [originalSettings, setOriginalSettings] = useState<AdminSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [phoneError, setPhoneError] = useState<string>('');

  // Fetch settings on mount when session is available
  useEffect(() => {
    if (session?.access_token) {
      fetchSettings();
    }
  }, [session?.access_token]);

  const fetchSettings = async () => {
    if (!session?.access_token) {
      console.error('No access token available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/admin/settings', {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSettings(data.settings);
          setOriginalSettings(data.settings);
        }
      } else {
        console.error('Failed to fetch settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) {
      setPhoneError('');
      return true; // Empty is valid (optional)
    }

    try {
      // Try to parse and validate
      if (!isValidPhoneNumber(phone, 'US')) {
        setPhoneError('Invalid phone number format. Use E.164 format (e.g., +1234567890)');
        return false;
      }

      const parsed = parsePhoneNumber(phone, 'US');
      if (parsed) {
        // Update to formatted version
        setSettings(prev => ({ ...prev, sms_phone_number: parsed.number }));
        setPhoneError('');
        return true;
      }
    } catch (error) {
      setPhoneError('Invalid phone number');
      return false;
    }

    setPhoneError('Invalid phone number');
    return false;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSettings(prev => ({ ...prev, sms_phone_number: value }));

    // Clear error when typing
    if (phoneError) {
      setPhoneError('');
    }
  };

  const handleToggle = (field: keyof AdminSettingsData) => {
    setSettings(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    // Validate phone number before saving
    if (settings.sms_phone_number && !validatePhoneNumber(settings.sms_phone_number)) {
      return;
    }

    if (!session?.access_token) {
      setMessage({ type: 'error', text: 'No authorization token available' });
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(settings),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        setOriginalSettings(settings);
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to save settings' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalSettings) {
      setSettings(originalSettings);
      setPhoneError('');
      setMessage(null);
    }
  };

  const handleTestSMS = async () => {
    if (!settings.sms_phone_number) {
      setMessage({ type: 'error', text: 'Please enter a phone number first' });
      return;
    }

    if (!validatePhoneNumber(settings.sms_phone_number)) {
      return;
    }

    if (!session?.access_token) {
      setMessage({ type: 'error', text: 'No authorization token available' });
      return;
    }

    try {
      setTesting(true);
      setMessage(null);

      const response = await fetch('/api/admin/test-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ phone_number: settings.sms_phone_number }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Test SMS sent! Check your phone.' });
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to send test SMS' });
      }
    } catch (error) {
      console.error('Error sending test SMS:', error);
      setMessage({ type: 'error', text: 'Failed to send test SMS' });
    } finally {
      setTesting(false);
    }
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Phone className="w-6 h-6 text-primary" />
        <div>
          <h2 className="text-2xl font-semibold text-white">SMS Notifications</h2>
          <p className="text-gray-400 text-sm mt-1">
            Configure SMS alerts for subscription events
          </p>
        </div>
      </div>

      {/* Message Banner */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-500/10 border border-green-500/20 text-green-400'
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Settings Form */}
      <div className="bg-dark-100 rounded-lg p-6 space-y-6">
        {/* Phone Number Input */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Phone Number
          </label>
          <div className="flex gap-2">
            <input
              type="tel"
              value={settings.sms_phone_number}
              onChange={handlePhoneChange}
              placeholder="+1234567890"
              className={`flex-1 px-4 py-2 bg-white border ${
                phoneError ? 'border-red-500' : 'border-gray-300'
              } rounded-lg text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary`}
            />
            <button
              onClick={handleTestSMS}
              disabled={testing || !settings.sms_phone_number}
              className="px-4 py-2 bg-dark-200 hover:bg-dark-300 border border-dark-300 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              title="Send test SMS"
            >
              <TestTube className="w-4 h-4" />
              Test
            </button>
          </div>
          {phoneError && (
            <p className="text-red-400 text-sm mt-1">{phoneError}</p>
          )}
          <p className="text-gray-500 text-sm mt-1">
            Use E.164 format (e.g., +1234567890). Leave empty to disable SMS notifications.
          </p>
        </div>

        {/* Notification Toggles */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-white">Notification Types</h3>

          {/* Trial Notification Toggle */}
          <div className="flex items-center justify-between p-4 bg-dark-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-white font-medium">Trial Started</p>
                <p className="text-gray-400 text-sm">
                  Notify when a user starts a free trial
                </p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('notify_on_trial')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.notify_on_trial ? 'bg-primary' : 'bg-dark-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.notify_on_trial ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Payment Notification Toggle */}
          <div className="flex items-center justify-between p-4 bg-dark-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-white font-medium">First Payment</p>
                <p className="text-gray-400 text-sm">
                  Notify when a user makes their first payment
                </p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('notify_on_payment')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.notify_on_payment ? 'bg-primary' : 'bg-dark-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.notify_on_payment ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Cancellation Notification Toggle */}
          <div className="flex items-center justify-between p-4 bg-dark-200 rounded-lg">
            <div className="flex items-center gap-3">
              <BellOff className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-white font-medium">Cancellation</p>
                <p className="text-gray-400 text-sm">
                  Notify when a user cancels their subscription
                </p>
              </div>
            </div>
            <button
              onClick={() => handleToggle('notify_on_cancellation')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.notify_on_cancellation ? 'bg-primary' : 'bg-dark-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.notify_on_cancellation ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        {hasChanges && (
          <div className="flex gap-3 pt-4 border-t border-dark-300">
            <button
              onClick={handleSave}
              disabled={saving || !!phoneError}
              className="flex-1 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-6 py-3 bg-dark-200 hover:bg-dark-300 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <h4 className="text-blue-400 font-medium mb-2">About SMS Notifications</h4>
        <ul className="text-gray-400 text-sm space-y-1">
          <li>• SMS notifications are sent via Telnyx ($0.004 per message)</li>
          <li>• Messages are only sent when toggles are enabled</li>
          <li>• Test SMS feature helps verify your number is configured correctly</li>
          <li>• All notifications are non-blocking and won't affect user actions</li>
        </ul>
      </div>
    </div>
  );
};
